/** The v1 device recorder (decision 0051, docs/recorder-api.md): the pure
 * logic behind the start screen (R3 on the "Preekie device recorder"
 * canvas). No DOM, no network -- tested with Node (test/recorder.test.ts).
 * The level and echo maths are the device-check harness's own, proven on
 * real devices in phase 1. */
import { SOUND_THRESHOLD, echoVerdict, median } from "./device-check-core.js";
import type { LevelSample } from "./device-check-core.js";

export type RecordMode = "online" | "in_person";
export type CallSource = "tab" | "screen";
export type SurfaceObserved = "browser" | "monitor" | "window";

export const RECORDER_COPY = {
  title: "Record a meeting",
  lead: "Preekie listens through this computer. Nothing joins your call.",
  client: "Client",
  chooseClient: "Choose a client",
  clientQuestion: "Who is the meeting with?", // CAP-010: the client cards' heading (design; Prabash 27 Oct)
  recordingFor: "Recording for", // CAP-010: "Recording for <client>"
  change: "Change",
  xeroConnected: "Xero connected", // CAP-010 design: the client card's sub-line
  where: "Where is the meeting?",
  online: "Online call",
  onlineHint: "Teams, Meet, Zoom or any app",
  inPerson: "In person",
  inPersonHint: "One device on the table",
  callSource: "How should Preekie hear the call?",
  tab: "The call's browser tab (recommended)",
  // #297 (UAT round 2): a desktop Teams or Zoom call never appears in the tab picker, so the hint says where to go instead.
  tabHint: "Meet, Teams or Zoom open in a Chrome or Edge tab. Only that tab's sound is recorded. Using the Teams or Zoom app? Choose Entire Screen.",
  shareCancelledTab:
    "Sharing was cancelled. If the call is in a browser tab, press Check sound and choose that tab. If it's in the Teams or Zoom app, choose Entire Screen, for a desktop app.",
  shareCancelledScreen: "Sharing was cancelled. Press Check sound, choose Entire Screen, tick “Also share system audio” and press Share.",
  screen: "Entire Screen, for a desktop app",
  screenHint: "Teams or Zoom installed on this computer. Captures all sound on this computer.",
  soundCheck: "Sound check",
  checkSound: "Check sound",
  checkAgain: "Check again",
  shareAgain: "Share again",
  you: "You",
  callSound: "Call sound",
  hearingYou: "Hearing you",
  notHearingYou: "Not hearing you yet",
  hearingCall: "Hearing the call",
  notHearingCall: "Not hearing the call yet",
  echoTest: "Check for echo",
  echoRunning: "Stay quiet for 5 seconds while the call plays…",
  echoNone: "No echo: your microphone isn't picking up the call.",
  echoHeard: "Your microphone heard the call. Wear headphones, or the notes may repeat what others said.",
  echoNotRun: "Echo couldn't be checked: nobody was speaking on the call. Ask someone to talk, then check again.",
  headphones: "I'm wearing headphones.",
  headphonesWhy: "Entire Screen records the call through your speakers too. Headphones keep it to one copy.",
  sharingStopped: "Sharing stopped, so Preekie can't hear the call.",
  beforeYouStart: "Before you start",
  // The three consent texts are the lawyer-approved wording (Prabash, 2026-10-19). Don't edit them:
  // a change needs the lawyer, and a new CONSENT_TEXT_VERSION here and in services/meetings/recorder.py.
  // Where Pause is offered, the notice is CONSENT_VERSIONS.withPause's instead (below).
  notice:
    "Before we start: I'd like to record this meeting with Preekie so I can prepare accurate notes. The recording is transcribed in Australia and then deleted, and I review the notes myself. Is everyone happy for me to record?",
  showOrRead: "Read this out to everyone before you tick.",
  consent:
    "I confirm that everyone in this meeting knows it is being recorded and has agreed, and that I will tell anyone who joins later.",
  calendarNotice:
    "This meeting may be recorded with Preekie to help us prepare notes. Let us know before the meeting if you would prefer it not to be recorded.",
  calendarHeading: "For the calendar invite",
  calendarHint: "Add this to the meeting invite, so people can say beforehand if they'd prefer it not to be recorded.",
  copy: "Copy",
  copied: "Copied",
  copyFailed: "Couldn't copy. Select the text and copy it instead.",
  start: "Start recording",
  next: "What happens next",
  nextSteps: [
    "Hold your meeting as usual.",
    "When you stop, the audio is transcribed in Australia.",
    "Preekie prepares draft notes: decisions, actions, figures and open questions, each with the words that support it.",
    "You check them and approve. Nothing is shared until you do.",
  ],
  deleted: "The recording is deleted as soon as the transcript exists.",
} as const;

/** Before the share picker, on the Entire Screen route only (Prabash, 2026-10-17; session D's wording). */
export const ENTIRE_SCREEN_NOTICE =
  "Entire Screen records every sound this computer plays until you press Stop: the call, other calls, notifications, videos. " +
  "Close or mute anything that shouldn't be recorded. Your screen itself is never recorded or uploaded.";

/** "Demo Company (AU)" -> "DC": the first letters of the first two words (brackets and punctuation skipped). */
export function clientInitials(name: string): string {
  const words = name.replace(/[^A-Za-z0-9 ]+/g, " ").trim().split(/\s+/).filter(Boolean);
  return (words.slice(0, 2).map((w) => w[0]!.toUpperCase()).join("") || "?");
}

/** The consent notice's version, stored with the consent record (docs/recorder-api.md). */
export const NOTICE_VERSION = "2026-10-19";

/** The version of the consent wording above, stored with the consent record and in the audit event. */
export const CONSENT_TEXT_VERSION = "2026-10-19";

/** The consent wording's two approved versions; the one shown is the one sent and stored (Prabash, 2026-10-24, option (b)). */
export const CONSENT_VERSIONS = {
  /** Wherever Pause isn't offered (its switch off, and today's console): the 19 Oct wording, unchanged. */
  standard: { version: CONSENT_TEXT_VERSION, notice: RECORDER_COPY.notice },
  /** Only where Pause is offered: RJ Legal's pause sentence (Prabash, 2026-10-24; word for word) just before the closing question. */
  withPause: {
    version: "2026-10-24",
    notice:
      "Before we start: I'd like to record this meeting with Preekie so I can prepare accurate notes. The recording is transcribed in Australia and then deleted, and I review the notes myself. The host may pause and continue this recording. Your consent covers the whole meeting, including any part recorded after a pause. Is everyone happy for me to record?",
  },
} as const;

/** Which consent this start screen shows (and sends): the pause wording only where Pause will be offered. */
export function consentFor(pauseOffered: boolean): { version: string; notice: string } {
  return pauseOffered ? CONSENT_VERSIONS.withPause : CONSENT_VERSIONS.standard;
}

/** What the browser does to the microphone before Preekie hears it (CAP-008.WP12, #272). Online, the microphone is the accountant alone
 * next to their own speakers: echo cancellation, noise suppression and automatic gain keep the call out of it and the voice level. In
 * person, one microphone has to hear several people at different distances, and those same processors are tuned for ONE near talker: they
 * treat a farther voice as noise or turn it down, so the second voice nearly vanishes before diarization sees it (found in UAT part 2,
 * 20 Oct 2026: about one voice through Preekie, two on the phone's own raw recording). In person there is no call to cancel, so all three
 * are off, which is what a plain voice recorder gives. */
export function micConstraints(mode: RecordMode): MediaTrackConstraints {
  const on = mode === "online";
  return { echoCancellation: on, noiseSuppression: on, autoGainControl: on };
}

/** getDisplayMedia options per route. Chrome needs video: true to share at
 * all; the video track is stopped and removed the moment it arrives
 * (keepAudioOnly). Tab: the browser surface is offered first and system
 * audio is left out. Entire Screen: the monitor, with system audio. */
export function displayMediaOptions(source: CallSource): Record<string, unknown> {
  return {
    video: { displaySurface: source === "tab" ? "browser" : "monitor" },
    audio: { suppressLocalAudioPlayback: false },
    systemAudio: source === "screen" ? "include" : "exclude",
    selfBrowserSurface: "exclude",
    // No switching from Chrome's own bar mid-recording: a different tab or
    // screen would skip the sound check and the notice. Share again instead.
    surfaceSwitching: "exclude",
    monitorTypeSurfaces: source === "screen" ? "include" : "exclude",
    preferCurrentTab: false,
  };
}

interface TrackLike {
  kind: string;
  stop(): void;
  getSettings?(): { displaySurface?: string };
}
interface StreamLike {
  getVideoTracks(): TrackLike[];
  getAudioTracks(): TrackLike[];
  removeTrack(t: TrackLike): void;
}

/** Stops and removes every video track from a screen share at once, before
 * anything else sees the stream, and returns what was shared. No frame is
 * ever kept, previewed or uploaded (session D, condition b). */
export function keepAudioOnly(stream: StreamLike): { surface: SurfaceObserved | null; audioTracks: number } {
  const videos = stream.getVideoTracks();
  const raw = videos[0]?.getSettings?.()?.displaySurface ?? null;
  for (const v of videos) {
    v.stop();
    stream.removeTrack(v);
  }
  const surface = raw === "browser" || raw === "monitor" || raw === "window" ? raw : null;
  return { surface, audioTracks: stream.getAudioTracks().length };
}

/** What went wrong with the microphone or the share, and what to do. The
 * recorder never asks for a camera, so unlike the meeting room's wording
 * (meetings.ts) this never mentions one. */
export function recorderMediaError(err: unknown): string {
  switch ((err as { name?: string } | null)?.name ?? "") {
    case "NotAllowedError":
    case "PermissionDeniedError":
      return "Your browser blocked the microphone. Allow it in the address bar, then press Check sound again.";
    case "NotFoundError":
    case "DevicesNotFoundError":
      return "No microphone was found. Plug one in, then press Check sound again.";
    case "NotReadableError":
    case "TrackStartError":
      return "Another app is using your microphone. Close it, then press Check sound again.";
    case "SecurityError":
      return "Your browser only allows the microphone on a secure (https) page.";
    case "AbortError":
      return "The microphone or the share stopped unexpectedly. Press Check sound again.";
    default:
      return "Preekie couldn't start your microphone. Check your browser's settings, then press Check sound again.";
  }
}

/** The route that matches what was actually shared. The picker can still
 * offer a tab on the Entire Screen route (and a screen on the tab route), so
 * the route follows the surface: the notice, the headphones rule and the
 * consent record's call_source then describe what's really captured. */
export function routeForSurface(source: CallSource, surface: SurfaceObserved | null): CallSource {
  if (surface === "browser") return "tab";
  if (surface === "monitor") return "screen";
  return source;
}

export const ROUTE_CHANGED: Record<CallSource, string> = {
  tab: "You shared a browser tab, so Preekie records only that tab's sound.",
  screen: "You shared your entire screen, so Preekie records all sound on this computer. Read the notice below.",
};

/** Sharing again mid-recording (Prabash, 2026-10-17): only the same kind of
 * source the recording started with -- tab for tab, Entire Screen for
 * Entire Screen. Anything else is stopped unused; switching kind means Stop
 * and a new recording with fresh consent. */
export type Reshare =
  | { ok: true }
  | { ok: false; reason: "other_kind" | "window" | "no_audio"; message: string };

export function reshareVerdict(original: CallSource, shared: { surface: SurfaceObserved | null; audioTracks: number }): Reshare {
  if (shared.surface === "window")
    return { ok: false, reason: "window", message: "A single window shares no sound, so Preekie didn't use it. " + RESHARE_COPY[original].again };
  const kind = routeForSurface(original, shared.surface);
  if (kind !== original) return { ok: false, reason: "other_kind", message: RESHARE_COPY[original].otherKind };
  if (shared.audioTracks === 0)
    return {
      ok: false,
      reason: "no_audio",
      message:
        original === "tab"
          ? "No sound was shared. Share the call's tab with “Also share tab audio” turned on."
          : "No sound was shared. Choose Entire Screen and tick “Also share system audio”.",
    };
  return { ok: true };
}

/** R4a/R4b on the recorder canvas. */
export const RESHARE_COPY: Record<CallSource, { stopped: string; button: string; again: string; otherKind: string; switchKind: string }> = {
  tab: {
    stopped: "Sharing stopped, so Preekie can't hear the call. Your voice is still being recorded. Share the call's browser tab again to pick up the call where it left off.",
    button: "Share the call's tab again",
    again: "Share the call's browser tab again.",
    otherKind: "You shared your entire screen, so Preekie didn't use it. This recording started with a browser tab and can only continue with a tab. Nothing from the screen was recorded.",
    switchKind: "Only a browser tab can be shared again in this recording. To record Entire Screen instead, stop and start a new recording; everyone will need to agree again.",
  },
  screen: {
    stopped: "Sharing stopped, so Preekie can't hear the call. Your voice is still being recorded. Share Entire Screen again to pick up the call where it left off.",
    button: "Share Entire Screen again",
    again: "Share Entire Screen again.",
    otherKind: "You shared a browser tab, so Preekie didn't use it. This recording started with Entire Screen and can only continue with Entire Screen. Nothing from the tab was recorded.",
    switchKind: "To record just the call's tab instead, stop and start a new recording; everyone will need to agree again.",
  },
};

/** Start stays off until the recording screen exists (session D, #218):
 * no consent record or token is created that nothing would use. */
export const NOT_YET_AVAILABLE = "Recording itself arrives in the next update. You can check sound now.";

export type Heard = "ok" | "silent" | "not_run";
export type Echo = "none" | "detected" | "not_run";

/** Heard means at least 3 samples above the harness's sound threshold. */
export function heard(levels: readonly number[]): Heard {
  if (levels.length === 0) return "not_run";
  return levels.filter((l) => l > SOUND_THRESHOLD).length >= 3 ? "ok" : "silent";
}

/** The echo test: the harness's verdict over the quiet window. */
export function echoResult(samples: readonly LevelSample[], fromS: number, toS: number): Echo {
  const v = echoVerdict(samples, fromS, toS).verdict;
  return v === "fail" ? "detected" : v === "pass" ? "none" : "not_run";
}

/** A 0..1 meter width from recent levels (louder speech reads near full). */
export function meterValue(recent: readonly number[]): number {
  if (!recent.length) return 0;
  return Math.max(0, Math.min(1, median(recent.slice(-6)) * 6));
}

export interface StartState {
  clientSlug: string;
  mode: RecordMode;
  source: CallSource;
  mic: Heard;
  call: Heard | "ended" | "no_track";
  surface: SurfaceObserved | null;
  echo: Echo;
  headphones: boolean;
  consent: boolean;
}

/** Why Start can't be pressed yet, in plain words, in the order to fix them. */
export function startBlockers(s: StartState): string[] {
  const out: string[] = [];
  if (!s.clientSlug) out.push("Choose a client.");
  if (s.mic !== "ok") out.push("Check sound: Preekie needs to hear your microphone.");
  if (s.mode === "online") {
    if (s.call === "ended") out.push(`${RECORDER_COPY.sharingStopped} Share the call again.`);
    else if (s.surface === "window")
      out.push("A single window shares no sound. Share the call's browser tab, or Entire Screen with “Also share system audio” ticked.");
    else if (s.call === "no_track")
      out.push(
        s.source === "tab"
          ? "No call sound was shared. Share the call's tab with “Also share tab audio” turned on."
          : "No call sound was shared. Choose Entire Screen and tick “Also share system audio”."
      );
    else if (s.call !== "ok") out.push("Check sound: Preekie needs to hear the call. Make sure someone is speaking.");
    // Entire Screen needs the headphones tick, always (Terms v1.1 section 6). A clean echo check is no substitute.
    if (s.source === "screen" && !s.headphones)
      out.push("Wear headphones and tick that you are: Entire Screen records your speakers too.");
  }
  if (!s.consent) out.push("Read the notice out, then tick the consent confirmation.");
  return out;
}

/** The body of POST /api/recorder (docs/recorder-api.md). */
/** `consentVersion`: the version of the wording the screen showed (consentFor); the 19 Oct one unless given. */
export function startBody(s: StartState, opts: { title: string; mimeType: string; chunkSeconds?: number; consentVersion?: string }): Record<string, unknown> {
  const online = s.mode === "online";
  return {
    client_slug: s.clientSlug,
    meeting_id: null,
    title: opts.title,
    mode: s.mode,
    call_source: online ? s.source : null,
    surface_observed: online ? s.surface : null,
    tracks: online ? ["mic", "call"] : ["mic"],
    mime_type: opts.mimeType,
    chunk_seconds: opts.chunkSeconds ?? 10,
    consent: { notice_version: opts.consentVersion ?? NOTICE_VERSION, consent_text_version: opts.consentVersion ?? CONSENT_TEXT_VERSION, attested: s.consent },
    sound_check: {
      mic: s.mic,
      call: online ? (s.call === "ok" ? "ok" : s.call === "not_run" ? "not_run" : "silent") : "not_run",
      echo: online ? s.echo : "not_run",
      headphones_confirmed: online && s.source === "screen" ? s.headphones : false,
    },
  };
}

// ---- While recording (R4, R6), the unsent banner (R8) and Discard ----

/** #205's stop confirmation, carried into the recorder (Prabash, 2026-10-16). */
export const STOP_CONFIRM = {
  question: "Stop recording? The meeting notes will be prepared from what's recorded so far.",
  stop: "Stop",
  keep: "Keep recording",
} as const;

export const LIVE_COPY = {
  keepOpen: "Keep this tab open while you record",
  recording: "Recording",
  recordingAll: "Recording all computer sound",
  voiceOnly: "Recording your voice only",
  you: "You",
  microphone: "Microphone",
  call: "Call",
  allSound: "All computer sound",
  notShared: "Not shared",
  stopRecording: "Stop recording",
  stopping: "Stopping…",
  callSilent: (minutes: number) =>
    `No call sound for ${minutes} minute${minutes === 1 ? "" : "s"}. If others are speaking, check the call is playing through this computer, not a phone or another device.`,
  signedOut: "Your sign-in ended, so uploads are paused. Recording carries on. Sign in again in another tab, then press Resume uploads.",
  resume: "Resume uploads",
  storage: "This computer refused to save the recording. Preekie is holding it in memory: free some disk space, and keep this tab open.",
  uploading: "Uploading the last of the recording. Keep this tab open.",
  uploadsPaused: "Uploads paused, will retry. The recording is safe on this computer: keep this tab open.",
  done: "Recording stopped. Preekie is preparing the notes.",
  quarantined: "The recording stopped, but the upload didn't check out, so it's held for review. Nothing will be shared.",
  openNotes: "Open the meeting",
} as const;

/** CAP-009.H11b Pause / Continue (A s.21; D's conditions). The new console only. */
export const PAUSE_COPY = {
  pause: "Pause",
  pausing: "Pausing…",
  paused: "Paused",
  pausedUnsaved: "Paused (not yet saved)",
  unsavedNote: "Nothing is being recorded. Preekie hasn't been able to tell the server about this pause yet and keeps trying.",
  continue: "Continue recording",
  // Asked before every Continue (RJ Legal, Prabash 2026-10-24; word for word).
  checkQuestion: "Has anyone joined since you paused? If so, read the consent message to them before you continue.",
  readAgain: "Read consent message again",
  continuing: "Waiting for you to choose…",
  nothingRecorded: "Nothing is being recorded. The microphone is off.",
  endsAt: (at: string) => `If it stays paused for 30 minutes, the recording ends at ${at}. Locking this device or leaving this page while paused ends it too.`,
  reshare: "To continue, share the call again: choose the same kind as before.",
  expired: "The recording was paused for more than 30 minutes, so it has ended. Start a new recording.",
  tooMany: "This recording has been paused 20 times, the most it can be. Stop it when the meeting ends.",
} as const;

/** 00:42:17 */
export function formatElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}

/** What the heading says: the coral dot plus one of three states. */
export function liveTitle(mode: RecordMode, source: CallSource | null, callShared: boolean): string {
  if (mode === "online" && !callShared) return LIVE_COPY.voiceOnly;
  if (mode === "online" && source === "screen") return LIVE_COPY.recordingAll;
  return LIVE_COPY.recording;
}

/** Whole minutes for people: at least 1 when anything is waiting. */
export function wholeMinutes(minutes: number): number {
  return minutes <= 0 ? 0 : Math.max(1, Math.round(minutes));
}

function plural(n: number, one: string): string {
  return `${n} ${one}${n === 1 ? "" : "s"}`;
}

/** 17/10/2026, 10:02 am in the viewer's zone. */
export function formatStarted(iso: string, timeZone: string): string {
  const d = new Date(iso);
  const date = new Intl.DateTimeFormat("en-AU", { timeZone, day: "2-digit", month: "2-digit", year: "numeric" }).format(d);
  const time = new Intl.DateTimeFormat("en-AU", { timeZone, hour: "numeric", minute: "2-digit", hour12: true }).format(d).replace(/\s/g, " ").toLowerCase();
  return `${date}, ${time}`;
}

/** Hours left before the device copy is deleted (rounded up, at least 1). */
export function hoursLeft(expiresAt: string, now: number): number {
  return Math.max(1, Math.ceil((Date.parse(expiresAt) - now) / 3_600_000));
}

/** The R8 banner for one recording waiting on this computer. Built only from
 * what the device holds -- minutes, start time, expiry -- and never a client
 * name (Prabash, 2026-10-17: nothing from device data names a client). */
export function unsentBanner(r: { started_at: string; unsent: { minutes: number; expires_at: string; warn_24h: boolean } }, timeZone: string, now: number): {
  level: "info" | "warn";
  text: string;
} {
  const mins = wholeMinutes(r.unsent.minutes);
  const started = formatStarted(r.started_at, timeZone);
  if (r.unsent.warn_24h) {
    return {
      level: "warn",
      text: `${plural(mins, "minute")} not yet uploaded, deleted from this computer in ${plural(hoursLeft(r.unsent.expires_at, now), "hour")}. From a recording started ${started}. Sign in on this computer with internet access to upload them before then.`,
    };
  }
  return { level: "info", text: `${plural(mins, "minute")} not yet uploaded from a recording started ${started} on this computer.` };
}

/** Discard's second confirmation names the minutes (session D, condition c). */
export function discardCopy(minutes: number): { question: string; keepUploaded: string; confirm: string; cancel: string } {
  const mins = wholeMinutes(minutes);
  return {
    question:
      mins === 1
        ? "Discard 1 minute that hasn't been uploaded? It can't be recovered, and the notes will be prepared without it."
        : `Discard ${mins} minutes that haven't been uploaded? They can't be recovered, and the notes will be prepared without them.`,
    keepUploaded: "Keep what's already uploaded and prepare notes from it",
    confirm: `Discard ${plural(mins, "minute")}`,
    cancel: "Keep them",
  };
}

/** Signing out while audio waits (session D, condition c). */
export function signOutCopy(recording: boolean, minutes: number): { question: string; confirm: string; cancel: string } | null {
  if (!recording && minutes <= 0) return null;
  const mins = wholeMinutes(minutes);
  const what = recording
    ? "You're recording. Signing out stops the recording"
    : `${plural(mins, "minute")} of a recording ${mins === 1 ? "hasn't" : "haven't"} been uploaded yet. Signing out pauses the upload`;
  return {
    question: `${what}; what's on this computer is uploaded the next time you sign in here, within 72 hours of the start.`,
    confirm: recording ? "Stop and sign out" : "Sign out anyway",
    cancel: recording ? "Keep recording" : "Stay signed in",
  };
}

export type DiagnosticPlatform = "windows-chrome" | "windows-edge" | "mac-chrome" | "mac-edge" | "mac-safari" | "ios-safari" | "android-chrome" | "other";

/** A coarse platform for the diagnostics route's allow-list -- never the user agent itself. */
export function platformTag(ua: string): DiagnosticPlatform {
  const ios = /iPhone|iPad|iPod/.test(ua);
  const android = /Android/.test(ua);
  const win = /Windows/.test(ua);
  const mac = !ios && /Macintosh|Mac OS X/.test(ua);
  const edge = /Edg\//.test(ua);
  const chrome = !edge && /Chrome\/|CriOS/.test(ua) && !/SamsungBrowser|OPR\//.test(ua);
  const safari = !chrome && !edge && /Safari\//.test(ua) && !/CriOS|FxiOS/.test(ua);
  if (ios && safari) return "ios-safari";
  if (android && chrome) return "android-chrome";
  if (win && edge) return "windows-edge";
  if (win && chrome) return "windows-chrome";
  if (mac && edge) return "mac-edge";
  if (mac && chrome) return "mac-chrome";
  if (mac && safari) return "mac-safari";
  return "other";
}

const SAFE_ERRORS = ["NotAllowedError", "NotFoundError", "NotReadableError", "OverconstrainedError", "AbortError", "SecurityError", "TypeError", "NetworkError", "QuotaExceededError"];

/** The diagnostics body: fixed values only, nothing a person typed (the server keeps only its allow-list). */
export function diagnosticsBody(
  outcome: "started" | "ended" | "resumed" | "upload_error",
  err: unknown,
  ua: string,
  s: Pick<StartState, "mic" | "call" | "echo"> | null
): Record<string, unknown> {
  const name = (err as { name?: unknown } | null)?.name;
  const body: Record<string, unknown> = { outcome, error_name: typeof name === "string" && SAFE_ERRORS.includes(name) ? name : null, platform: platformTag(ua) };
  if (s) body.sound_check = { mic: s.mic === "ok" ? "ok" : "silent", call: s.call === "ok" ? "ok" : s.call === "not_run" ? null : "silent", echo: s.echo };
  return body;
}

/** The engine's error codes in plain words: never a raw code on screen. */
export function engineErrorText(code: string, message: string): string {
  switch (code) {
    case "upload_refused":
      return "Preekie couldn't upload part of the recording. It's kept on this computer: keep this tab open and Preekie will try again.";
    case "storage_full":
      return "This computer can't hold more audio, so recording is paused until what's waiting uploads. Keep this tab open.";
    case "not_audio_only":
      return "Preekie couldn't start recording. Press Stop, then start again.";
    default:
      return /^[a-z_]+$/.test(message) || !message ? "Something went wrong with the recording. Keep this tab open." : message;
  }
}

/** A recording still open on the server but not on this computer (another
 * browser or device, or its storage was cleared). While it's open no new
 * recording can start, so the start screen says so and offers Discard. */
export function openElsewhereCopy(startedAt: string | null, timeZone: string): { text: string; discard: string; question: string; confirm: string; cancel: string } {
  const when = startedAt ? ` (started ${formatStarted(startedAt, timeZone)})` : "";
  return {
    text: `A recording you started is still open${when}, so a new one can't start yet. If it's still recording or uploading on another computer or browser, finish it there. Otherwise discard it here.`,
    discard: "Discard it…",
    question: "Discard the open recording? Whatever already reached Preekie is kept and the notes are prepared from it. Audio still on another computer won't be used.",
    confirm: "Discard it",
    cancel: "Keep it open",
  };
}

/** Discard didn't go through (CAP-008 #266: it used to fail silently and leave the recording blocking Start). */
export const DISCARD_FAILED = "Preekie couldn't discard that recording. Nothing was lost: try again in a moment, and if it still fails, tell us.";

// ---- Signed out (CAP-008 #303, Prabash BR-0020) ----
// The 30-minute idle sign-out stays as it is. The recorder checks the session before the sound check and consent, and any
// 401 on a recorder action says so, with a way to sign in, rather than failing at Start or silently.

export const SIGNED_OUT = {
  message: "You've been signed out. Sign in again to record.",
  action: "Sign in",
  // Discard pressed during a recording: signing in here would leave the page, so another tab.
  whileRecording: "You've been signed out. Sign in again in another tab, then press Discard again.",
} as const;

/** A recorder action refused because the console session has ended (the API's ApiError or the engine's ApiFailure). */
export function isSignedOut(err: unknown): boolean {
  return (err as { status?: unknown } | null)?.status === 401;
}

/** The session check the start screen runs first: only a 401 means signed out. Anything else (offline, a 5xx) lets the
 * person carry on; Start reports its own error, and a 401 there shows the same signed-out panel. */
export async function sessionState(check: () => Promise<unknown>): Promise<"ok" | "signed_out"> {
  try {
    await check();
    return "ok";
  } catch (err) {
    return isSignedOut(err) ? "signed_out" : "ok";
  }
}

// ---- The recording cap (CAP-008.WP11, Prabash 2026-10-20): warn at 50 minutes, stop cleanly at 55 ----
// The numbers come from the server (services/meetings/recorder.py), never from here: /api/me's
// recording_limits for the start screen, and the start response's recording_warn_seconds and
// recording_limit_seconds for the live screen. No numbers from the server, no cap shown.

export interface RecordingLimits {
  limitSeconds: number;
  warnSeconds: number;
}

/** The start response's limit fields, if the server sent sound ones. */
export function limitsFromStart(started: Record<string, unknown>): RecordingLimits | null {
  const limit = started.recording_limit_seconds;
  const warn = started.recording_warn_seconds;
  if (typeof limit !== "number" || typeof warn !== "number" || !(limit > 0) || !(warn > 0) || warn >= limit) return null;
  return { limitSeconds: limit, warnSeconds: warn };
}

export type LimitState = "none" | "warning" | "stopped";

/** Where a recording is against the cap, from the engine's elapsed time. */
export function limitState(elapsedMs: number, limits: RecordingLimits | null): LimitState {
  if (!limits) return "none";
  if (elapsedMs >= limits.limitSeconds * 1000) return "stopped";
  if (elapsedMs >= limits.warnSeconds * 1000) return "warning";
  return "none";
}

function minutesWord(n: number): string {
  return `${n} minute${n === 1 ? "" : "s"}`;
}

/** The start screen's line, from /api/me's recording_limits. */
export function limitLine(limitMinutes: number | null | undefined): string | null {
  if (typeof limitMinutes !== "number" || !(limitMinutes > 0)) return null;
  return `Recordings stop automatically at ${minutesWord(limitMinutes)}. For a longer meeting, start a new recording when this one stops.`;
}

/** What the live screen says at the warning, and after the automatic stop. */
export function limitCopy(limits: RecordingLimits): { warning: string; stopped: string } {
  const left = Math.round((limits.limitSeconds - limits.warnSeconds) / 60);
  const at = formatElapsed(limits.limitSeconds * 1000).replace(/^00:/, "");
  const total = Math.round(limits.limitSeconds / 60);
  return {
    warning: `${minutesWord(left)} left. The recording stops automatically at ${at}. To keep going after that, start a new recording.`,
    stopped: `Stopped automatically at ${minutesWord(total)}, the longest one recording can run. Preekie is preparing the notes from what was recorded.`,
  };
}

/** What the live screen does when the cap state may have changed. The engine (#263) and the screen's own clock both
 * report the cap, so the same state can arrive twice: only the first change acts, the recording is stopped at most
 * once, and nothing goes back from "stopped" (session D, #264). */
export function limitAction(
  current: LimitState,
  next: LimitState,
  hasLimits: boolean,
  alreadyStopping: boolean
): { state: LimitState; act: "none" | "show_warning" | "stop" } {
  if (!hasLimits || alreadyStopping || next === current || next === "none") return { state: current, act: "none" };
  if (current === "stopped") return { state: current, act: "none" };
  if (next === "warning") return { state: "warning", act: "show_warning" };
  return { state: "stopped", act: "stop" };
}

export const RECORD_AGAIN = "Record again";

/** What to say when the share picker is cancelled (#297): on the tab route, the call may be in a desktop app that the tab
 * picker never lists, so point to Entire Screen instead of back to the same picker. */
export function shareCancelledMessage(source: CallSource): string {
  return source === "tab" ? RECORDER_COPY.shareCancelledTab : RECORDER_COPY.shareCancelledScreen;
}
